/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  cleanCoords,
  difference,
  featureCollection,
  lineSplit,
} from '@turf/turf';
import type { FeatureCollection, Feature, Position } from 'geojson';
import type { Converter } from 'proj4';
import proj4 from 'proj4';
import { polygonFeatureCollectionCleanDangling } from './polygonCleanDangling';

export type ProjectorCache = Record<string, Converter>;

// Cache for for storing and reusing Proj4 instances
export const projectorCache = {} as ProjectorCache;

// Ensure that you have a proj4 object, pulling from the cache if necessary
export const getProj4 = (projection: string | Converter): Converter => {
  // Duck typing: check for the 'forward' and 'inverse' methods which are present on proj4 projection objects
  if (
    typeof (projection as Converter).forward === 'function' &&
    typeof (projection as Converter).inverse === 'function'
  ) {
    return projection as Converter;
  }
  if (typeof projection === 'string' && projection in projectorCache) {
    return projectorCache[projection] as Converter;
  }
  projectorCache[projection as string] = proj4(projection as string);
  return projectorCache[projection as string] as Converter;
};

export const NEW_LINESTRING_CREATED = 'new point in LineString created';
export const NEW_FEATURE_CREATED = 'new feature created';
export const NEW_POINT_CREATED = 'new point created';

const roundToDecimals = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const roundCoordinates = <
  T extends Position | Position[] | Position[][] | Position[][][],
>(
  coords: T,
): T => {
  if (!Array.isArray(coords)) {
    return coords;
  }

  const mapCoords = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map(mapCoords);
    }
    if (typeof value === 'number') {
      return roundToDecimals(value, 5);
    }
    return value;
  };

  return mapCoords(coords) as T;
};

export const roundFeatureCoordinates = <T extends Feature>(feature: T): T => {
  if (feature.geometry.type === 'GeometryCollection') {
    // no coordinates
    return feature;
  }
  const roundedFeature = {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: roundCoordinates(feature.geometry.coordinates),
    },
  };

  return roundedFeature;
};

export const splitPolygon = (
  polygonFeature: Feature<GeoJSON.Polygon>,
  splitFeature: Feature<GeoJSON.Polygon>,
): FeatureCollection<GeoJSON.Polygon> | null => {
  try {
    return splitPolygonNoGuard(polygonFeature, splitFeature);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
    return null;
  }
};

const splitPolygonNoGuard = (
  polygonFeature: Feature<GeoJSON.Polygon>,
  splitFeature: Feature<GeoJSON.Polygon>,
): FeatureCollection<GeoJSON.Polygon> | null => {
  const { properties } = polygonFeature;
  const polygonRounded = roundFeatureCoordinates(polygonFeature);
  const splitRounded = roundFeatureCoordinates(splitFeature);
  const newPolygon = difference(
    featureCollection([polygonRounded, splitRounded]),
  );

  if (!newPolygon) {
    console.warn('no difference between polygons');
    return null;
  }

  let splitFeatureWithStyling = null;

  splitFeatureWithStyling = cleanCoords({
    ...splitRounded,
    properties,
  });

  if (newPolygon.geometry.type === 'MultiPolygon') {
    const newFeatures = newPolygon.geometry.coordinates.map((coords) => {
      const newFeature: Feature<GeoJSON.Polygon> = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: roundCoordinates(coords),
        },
        properties,
      };
      return cleanCoords(newFeature) as Feature<GeoJSON.Polygon>;
    });
    return polygonFeatureCollectionCleanDangling({
      type: 'FeatureCollection',
      features: [...newFeatures, splitFeatureWithStyling],
    });
  }

  const newFeature = cleanCoords({
    ...newPolygon,
    geometry: {
      ...newPolygon.geometry,
      coordinates: roundCoordinates(newPolygon.geometry.coordinates),
    },
    properties,
  }) as Feature<GeoJSON.Polygon>;

  const newGeoJson = {
    type: 'FeatureCollection',
    features: [newFeature, splitFeatureWithStyling],
  } as FeatureCollection<GeoJSON.Polygon>;
  return polygonFeatureCollectionCleanDangling(newGeoJson);
};

export const splitLine = (
  drawFeature: Feature<GeoJSON.LineString>,
  splitFeature: Feature<GeoJSON.LineString>,
): FeatureCollection<GeoJSON.LineString> | null => {
  const { properties } = drawFeature;
  const drawRounded = roundFeatureCoordinates(drawFeature);
  const splitRounded = roundFeatureCoordinates(splitFeature);
  const result = lineSplit(drawRounded, splitRounded);

  if (result.features.length < 2) {
    console.warn('Split failed', result);
    return null;
  }

  const newLines = result.features.map((feature) => {
    const newFeature: Feature<GeoJSON.LineString> = {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: roundCoordinates(feature.geometry.coordinates),
      },
      properties,
    };
    return cleanCoords(newFeature) as Feature<GeoJSON.LineString>;
  });

  return featureCollection(newLines);
};
